import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2, Check, ArrowRight, User, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function WaitlistForm({ defaultPersona = "individual", onJoined }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    persona: defaultPersona,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [doneMsg, setDoneMsg] = useState("");

  useEffect(() => {
    setForm((f) => ({ ...f, persona: defaultPersona }));
  }, [defaultPersona]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/waitlist", form);
      setDoneMsg(data.message || "You're on the list.");
      setDone(true);
      onJoined && onJoined(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Please enter a valid email address and try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="flex flex-col items-center gap-4 rounded-2xl border border-ember/30 bg-ember/5 px-6 py-10 text-center"
        data-testid="waitlist-success"
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ember text-paper">
          <Check size={26} />
        </span>
        <h3 className="font-display text-2xl text-paper">Priority access granted</h3>
        <p className="max-w-sm text-sm text-mute">{doneMsg}</p>
        <p className="font-mono text-[11px] uppercase tracking-overline text-ember">
          We'll reach out before the private beta opens.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="waitlist-form">
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setForm((f) => ({ ...f, persona: "individual" }))}
          className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm transition-[color,border-color,background-color] duration-300 ${
            form.persona === "individual"
              ? "border-ember bg-ember/10 text-paper"
              : "border-white/15 text-mute hover:border-white/30"
          }`}
          data-testid="waitlist-persona-individual"
        >
          <User size={15} /> Individual
        </button>
        <button
          type="button"
          onClick={() => setForm((f) => ({ ...f, persona: "enterprise" }))}
          className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm transition-[color,border-color,background-color] duration-300 ${
            form.persona === "enterprise"
              ? "border-ember bg-ember/10 text-paper"
              : "border-white/15 text-mute hover:border-white/30"
          }`}
          data-testid="waitlist-persona-enterprise"
        >
          <Building2 size={15} /> Enterprise
        </button>
      </div>

      <input
        type="text"
        placeholder="Full name"
        value={form.name}
        onChange={update("name")}
        className="w-full rounded-lg border border-white/20 bg-ink px-4 py-3 text-sm text-paper outline-none transition-colors placeholder:text-mute/70 focus:border-ember"
        data-testid="waitlist-name-input"
      />
      <input
        type="email"
        placeholder="Work / personal email"
        value={form.email}
        onChange={update("email")}
        className="w-full rounded-lg border border-white/20 bg-ink px-4 py-3 text-sm text-paper outline-none transition-colors placeholder:text-mute/70 focus:border-ember"
        data-testid="waitlist-email-input"
      />
      <input
        type="text"
        placeholder={
          form.persona === "enterprise"
            ? "Organisation"
            : "Organisation (optional)"
        }
        value={form.organization}
        onChange={update("organization")}
        className="w-full rounded-lg border border-white/20 bg-ink px-4 py-3 text-sm text-paper outline-none transition-colors placeholder:text-mute/70 focus:border-ember"
        data-testid="waitlist-org-input"
      />
      <textarea
        placeholder="What would you use Smritium for? (optional)"
        value={form.message}
        onChange={update("message")}
        rows={3}
        className="w-full resize-none rounded-lg border border-white/20 bg-ink px-4 py-3 text-sm text-paper outline-none transition-colors placeholder:text-mute/70 focus:border-ember"
        data-testid="waitlist-message-input"
      />

      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full disabled:opacity-60"
        data-testid="waitlist-submit-button"
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Joining…
          </>
        ) : (
          <>
            Request early access <ArrowRight size={16} />
          </>
        )}
      </button>
      <p className="text-center text-[11px] text-mute">
        No spam. We'll only email you about the Smritium beta.
      </p>
    </form>
  );
}
